import { useEffect, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import styles from './BobsDilemmaSection.module.css';

export const BobsDilemmaSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const shakeRef = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.08 });

  const { prefersReducedMotion } = useReducedMotion();

  useEffect(() => {
    const el = shakeRef.current;
    if (!el || prefersReducedMotion) return;

    const trigger = (): void => {
      el.classList.remove('is-shaking');
      void el.offsetHeight;
      el.classList.add('is-shaking');
    };

    const id = setInterval(trigger, 2000);

    return () => {
      clearInterval(id);
      el.classList.remove('is-shaking');
    };
  }, [prefersReducedMotion]);

  const systems = t('home.bobsDilemma.systems', { returnObjects: true }) as string[];

  return (
    <Section tone="default" id="dilemma">
      <Container size="wide">
        <div ref={ref}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow>{t('home.bobsDilemma.kicker')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.bobsDilemma.title')}
            </h2>
          </header>

          <div className={styles.layout}>
            <div className={styles.systemsCol} data-fade>
              <p className={styles.systemsLabel}>{t('home.bobsDilemma.systemsLabel')}</p>
              <ul className={styles.systemList}>
                {systems.map((s) => (
                  <li key={s} className={styles.systemItem}>
                    <span className={styles.systemDot} aria-hidden="true" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.calculationCol}>
              <div className={`${styles.calcRow} ${styles.calcRowDanger}`} data-fade>
                <p className={styles.calcLabel}>{t('home.bobsDilemma.required')}</p>
                <p
                  ref={shakeRef}
                  data-shake
                  className={`${styles.calcValue} ${styles.calcValueDanger}`}
                >
                  415
                </p>
                <p className={styles.calcUnit}>{t('home.bobsDilemma.requiredValue')}</p>
                <p className={styles.calcNote}>{t('home.bobsDilemma.requiredCalc')}</p>
              </div>

              <div className={`${styles.calcRow} ${styles.calcRowMuted}`} data-fade>
                <p className={styles.calcLabel}>{t('home.bobsDilemma.available')}</p>
                <p className={`${styles.calcValue} ${styles.calcValueBrand}`}>7</p>
                <p className={styles.calcUnit}>{t('home.bobsDilemma.availableValue')}</p>
              </div>

              <div className={`${styles.calcRow} ${styles.calcRowGap}`} data-fade>
                <p className={`${styles.calcLabel} ${styles.calcLabelDanger}`}>
                  {t('home.bobsDilemma.gapLabel')}
                </p>
                <p className={`${styles.calcValue} ${styles.calcValueDanger}`}>
                  {t('home.bobsDilemma.gapValue')}
                </p>
                <p className={styles.calcUnitSmall}>{t('home.bobsDilemma.requiredValue')}</p>
              </div>
            </div>
          </div>

          <p className={styles.footnote} data-fade>
            {t('home.bobsDilemma.footnote')}
          </p>
        </div>
      </Container>
    </Section>
  );
};

BobsDilemmaSection.displayName = 'BobsDilemmaSection';
